/**
 * Where the cash went.
 *
 * The indirect statement is an explanation, and an explanation that does not
 * account for the whole change in cash is not one. So the page ends in a
 * comparison rather than a total: what the sections say moved, against what
 * actually moved in the bank.
 *
 * When those differ, the page shows the report's own sentence rather than
 * inventing one, and lists the accounts that caused it. A statement that is out
 * by three lakh and says only "does not tie" sends somebody through a year of
 * postings; one that names the account is a thirty-second fix.
 *
 * It renders either way. A statement that refuses to appear until every account
 * is tagged is a statement nobody sees, and the untagged account stays
 * untagged.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, ErrorNote, Loading, Note } from '../components/ui'

interface Line {
  account_id: string
  code: string
  name: string
  movement_paise: number
}

interface Section {
  cash_flow_class: string
  lines: Line[]
  total_paise: number
}

interface Untagged {
  account_id: string
  code: string
  name: string
  account_type: string
  movement_paise: number
}

interface Statement {
  fy_code: string
  since: string
  upto: string
  sections: Section[]
  noncash_addback_paise: number
  opening_cash_paise: number
  closing_cash_paise: number
  explained_paise: number
  actual_paise: number
  difference_paise: number
  ties_out: boolean
  has_cash_accounts: boolean
  untagged: Untagged[]
  why_not: string
}

const HEADINGS: Record<string, string> = {
  operating: 'From trading',
  investing: 'From investing',
  financing: 'From financing',
}

function currentFy(): string {
  const now = new Date()
  const start = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`
}

export default function CashFlow() {
  const [fyCode, setFyCode] = useState(currentFy)

  const statement = useQuery({
    queryKey: ['cash-flow', fyCode],
    queryFn: () => api.get<Statement>(`/reports/cash-flow?fy_code=${fyCode}`),
  })

  const data = statement.data

  return (
    <>
      <ErrorNote error={statement.error} />

      <Card title="Period">
        <div className="row">
          <div>
            <span className="caption">Financial year</span>
            <input value={fyCode} onChange={(e) => setFyCode(e.target.value)} />
          </div>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          Positive means cash came in, throughout — so you never have to ask which
          convention a line is using.
        </p>
      </Card>

      {statement.isLoading ? (
        <Loading what="Working out where the cash went" />
      ) : (
        data && (
          <>
            {!data.ties_out && <Note kind="warn">{data.why_not}</Note>}

            {data.has_cash_accounts && data.untagged.length > 0 && (
              <Card title="Accounts with no cash-flow class" tight>
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Account</th>
                      <th>Kind</th>
                      <th className="num">Moved by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.untagged.map((a) => (
                      <tr key={a.account_id}>
                        <td className="mono">{a.code}</td>
                        <td>{a.name}</td>
                        <td className="muted">{a.account_type}</td>
                        <td className="num">{formatInr(a.movement_paise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="faint">
                  Set the class on each in the chart of accounts. Which bucket an account
                  belongs in is an accounting judgement about that account, so it is
                  recorded there once rather than decided by each report.
                </p>
              </Card>
            )}

            {data.sections.map((section) => (
              <Card
                key={section.cash_flow_class}
                title={HEADINGS[section.cash_flow_class] ?? section.cash_flow_class}
                hint={formatInr(section.total_paise)}
                tight
              >
                {section.lines.length === 0 ? (
                  <div className="empty">Nothing moved.</div>
                ) : (
                  <table>
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Account</th>
                        <th className="num">In / (out)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.lines.map((line) => (
                        <tr key={line.account_id}>
                          <td className="mono">{line.code}</td>
                          <td>{line.name}</td>
                          <td className="num">{formatInr(line.movement_paise)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={2}>
                          <strong>Total</strong>
                        </td>
                        <td className="num">
                          <strong>{formatInr(section.total_paise)}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </Card>
            ))}

            <Card title="Does it explain the movement?" tight>
              <table>
                <tbody>
                  <tr>
                    <td>Cash at the start</td>
                    <td className="num">{formatInr(data.opening_cash_paise)}</td>
                  </tr>
                  <tr>
                    <td>Cash at the end</td>
                    <td className="num">{formatInr(data.closing_cash_paise)}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>What actually moved</strong>
                    </td>
                    <td className="num">
                      <strong>{formatInr(data.actual_paise)}</strong>
                    </td>
                  </tr>
                  {data.noncash_addback_paise !== 0 && (
                    <tr>
                      <td className="muted">
                        Added back — costs that took no cash
                      </td>
                      <td className="num muted">
                        {formatInr(data.noncash_addback_paise)}
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      <strong>What the sections above explain</strong>
                    </td>
                    <td className="num">
                      <strong>{formatInr(data.explained_paise)}</strong>
                    </td>
                  </tr>
                  <tr>
                    <td>Unexplained</td>
                    <td className="num">
                      {data.ties_out ? (
                        <span className="faint">nil</span>
                      ) : (
                        <strong>{formatInr(data.difference_paise)}</strong>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </Card>
          </>
        )
      )}
    </>
  )
}
